import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/** Same gift-card rule as assets/wt-free-gift.js: gift cards don't count towards a cart reward. */
const isGiftCard = (/** @type {any} */ item) => {
  const type = String(item.product_type ?? '').trim().toLowerCase();
  return Boolean(item.gift_card) || type === 'gift card' || type === 'gift cards' || String(item.handle ?? '').includes('gift-card');
};

/**
 * Reward total as in assets/wt-free-gift.js: no gift cards, no bonus-build lines.
 * @param {any[]} items - cart.items from the Ajax Cart API
 * @param {string | undefined} rewardVariantIds - comma-separated variant ids of the bonus builds
 */
const qualifyingTotal = (items, rewardVariantIds) => {
  const rewardVariants = (rewardVariantIds ?? '')
    .split(',')
    .map(Number)
    .filter((id) => id > 0);
  return items
    .filter((item) => !rewardVariants.includes(Number(item.variant_id ?? item.id)) && !isGiftCard(item))
    .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
};

/**
 * True when a cart line other than a bonus build is discounted (automatic sale, code). The reward
 * counts lines after discounts, so an added kit may count less than its price: then no "unlocks"
 * claim is made (same rule as snippets/wt-reward-claims.liquid).
 * @param {any[]} items - cart.items from the Ajax Cart API
 * @param {string | undefined} rewardVariantIds - comma-separated variant ids of the bonus builds
 */
const hasDiscount = (items, rewardVariantIds) => {
  const rewardVariants = (rewardVariantIds ?? '')
    .split(',')
    .map(Number)
    .filter((id) => id > 0);
  return items.some(
    (item) =>
      !rewardVariants.includes(Number(item.variant_id ?? item.id)) &&
      (Number(item.final_line_price ?? 0) < Number(item.original_line_price ?? 0) ||
        (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0))
  );
};

/**
 * Updates the trust box reward line (blocks/wt-trust-badges.liquid, [data-reward-line]) for the
 * new cart: "This kit unlocks your …" or "Add $X more and get a …". The cart counts plus this kit,
 * unless the kit is already in the cart. Hidden when the rest of the cart already reaches the threshold,
 * and while the cart carries a discount (the kit's regular price would not be what it counts).
 * @param {any[]} items
 */
const updateRewardLines = (items) => {
  for (const line of document.querySelectorAll('[data-reward-line]')) {
    if (!(line instanceof HTMLElement)) continue;
    const threshold = Number(line.dataset.threshold || 0);
    if (!threshold) continue;

    const mainInCart = items.some((item) => String(item.product_id) === line.dataset.productId);
    const cartTotal = qualifyingTotal(items, line.dataset.rewardVariants);
    const base = mainInCart ? cartTotal : cartTotal + Number(line.dataset.mainPrice || 0);
    const gap = Math.max(threshold - base, 0);

    // The rest of the cart already qualifies: this kit unlocks nothing, so the line is hidden.
    // A discounted cart makes the gap inexact, so the line is hidden then too
    const others = items.filter((item) => String(item.product_id) !== line.dataset.productId);
    line.hidden =
      qualifyingTotal(others, line.dataset.rewardVariants) >= threshold || hasDiscount(items, line.dataset.rewardVariants);

    const unlocked = line.querySelector('[data-reward-unlocked]');
    const missing = line.querySelector('[data-reward-missing]');
    if (unlocked instanceof HTMLElement) unlocked.hidden = gap > 0;
    if (missing instanceof HTMLElement) missing.hidden = gap === 0;

    const amount = line.querySelector('[data-reward-gap]');
    if (amount && gap > 0) {
      // Same output as Liquid's money_without_trailing_zeros ($80, not $80.00)
      const formatted = formatMoney(gap, line.dataset.moneyFormat || '${{amount}}', line.dataset.currency || 'USD');
      amount.textContent = formatted.replace(/[.,]00(?!\d)/, '');
    }
  }
};

/**
 * One-click add-on for the product page buy box ("Pair it with").
 *
 * Buttons with data-main-variant add the current product and the add-on together ("Add both"). The main
 * variant is read from this product's own form (matched by product id inside the same section or
 * quick-add dialog), so a changed variant selection is respected.
 *
 * Follows the same contract as Horizon's product form: it dispatches a CartLinesUpdateEvent with a
 * deferred promise, adds the lines via the Ajax Cart API (requesting the cart section HTML), then
 * resolves the promise so the cart drawer, cart icon and reward progress bar update themselves.
 * It also listens to every cart update to keep "Add both", the reward badges and the trust box reward
 * line in sync with the cart.
 */
class WtAddonOffer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  /**
   * The variant currently selected in this product's form, falling back to the rendered one.
   * @param {string} fallback
   */
  #currentMainVariantId(fallback) {
    const scope = this.closest('dialog, .shopify-section') ?? document;
    const input = scope.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"] input[name="id"]`);
    return input instanceof HTMLInputElement && input.value ? input.value : fallback;
  }

  /** Remaining buttons only add their add-on once the main product is in the cart. */
  #switchToAddonOnly() {
    for (const button of this.querySelectorAll('[data-main-variant]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      delete button.dataset.mainVariant;
      // An added button keeps its "Added" text and its matching accessible name
      if (button.classList.contains('is-added')) continue;
      const label = button.querySelector('[data-addon-label]');
      if (label) label.textContent = this.dataset.addLabel || 'Add';
      button.setAttribute('aria-label', `${this.dataset.addLabel || 'Add'}: ${button.dataset.addonTitle ?? ''}`);
    }
  }

  /**
   * True when this product's own Add to cart button is disabled (sold-out variant selected).
   * Adding a sold-out main line would make Shopify reject the add-on too.
   */
  #mainVariantUnavailable() {
    const scope = this.closest('dialog, .shopify-section') ?? document;
    const button = scope.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"] button[name="add"]`);
    return button instanceof HTMLButtonElement && button.disabled;
  }

  /**
   * Re-evaluate "Add both" and the reward badges after any cart change on the page.
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    /** @type {Promise<any> | undefined} */
    const promise = /** @type {any} */ (event).promise;
    promise
      ?.then(({ detail }) => {
        const items = detail?.items;
        if (!Array.isArray(items)) return;

        const mainInCart = items.some((item) => String(item.product_id) === this.dataset.productId);
        if (mainInCart) this.#switchToAddonOnly();

        const threshold = Number(this.dataset.threshold || 0);
        if (!threshold) return;

        const cartTotal = qualifyingTotal(items, this.dataset.rewardVariants);
        const base = mainInCart ? cartTotal : cartTotal + Number(this.dataset.mainPrice || 0);
        // No "Unlocks…" claim while the cart carries a discount (see hasDiscount)
        const discounted = hasDiscount(items, this.dataset.rewardVariants);
        for (const badge of this.querySelectorAll('[data-unlock-for]')) {
          if (!(badge instanceof HTMLElement)) continue;
          const addonPrice = Number(badge.dataset.unlockFor || 0);
          badge.hidden = discounted || !(base < threshold && base + addonPrice >= threshold);
        }
      })
      .catch(() => {});
  };

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    const button = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-addon-variant]') : null
    );
    if (!button || button.disabled) return;

    const variantId = button.dataset.addonVariant;
    if (!variantId) return;

    const label = button.querySelector('[data-addon-label]');
    const mainVariantId =
      button.dataset.mainVariant && !this.#mainVariantUnavailable()
        ? this.#currentMainVariantId(button.dataset.mainVariant)
        : null;
    const lines = [
      ...(mainVariantId ? [{ id: Number(mainVariantId), quantity: 1 }] : []),
      { id: Number(variantId), quantity: 1 },
    ];

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }) })
      );
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      // Mark the button as added before resolving: the resolved promise runs #handleCartUpdate,
      // which must see is-added so it doesn't reset the accessible name to "Add: …".
      button.classList.add('is-added');
      delete button.dataset.mainVariant;
      if (label) label.textContent = this.dataset.addedLabel || 'Added';
      // Keep the accessible name in step with the visible text
      button.setAttribute('aria-label', `${this.dataset.addedLabel || 'Added'}: ${button.dataset.addonTitle ?? ''}`);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'wt-addon-offer',
          sourceId: this.id,
          itemCount: lines.length,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Add to cart failed',
          code: 'INVALID',
          detail: { source: 'wt-addon-offer' },
        })
      );
      button.disabled = false;
      if (label) label.textContent = this.dataset.errorLabel || 'Try again';
      button.setAttribute('aria-label', `${this.dataset.errorLabel || 'Try again'}: ${button.dataset.addonTitle ?? ''}`);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

/*
 * The trust box reward line follows every cart change, also on pages where the add-on block has
 * nothing to offer (blocks/wt-trust-badges loads this module for the line). Registered once per page:
 * a module script runs a single time however often it is included.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  /** @type {Promise<any> | undefined} */
  const promise = /** @type {any} */ (event).promise;
  promise
    ?.then(({ detail }) => {
      if (Array.isArray(detail?.items)) updateRewardLines(detail.items);
    })
    .catch(() => {});
});

if (!customElements.get('wt-addon-offer')) {
  customElements.define('wt-addon-offer', WtAddonOffer);
}
